import React from 'react'
import { withRouter } from 'react-router-dom'

import { DiscreteProgressBar, Ink } from '@acorns/web-components'
import { compose, withHandlers, withProps } from 'recompose'
import styled from 'styled-components'

import AcornsLogoGreenSVG from 'assets/icons/acorns_logo_green.svg'
import BackArrowSVG from 'assets/icons/arrow-left.svg'
import { Accessibility } from 'components/Accessibility'
import withHeroText from 'components/HOCs/withHeroText'
import withViewportMeta, { State as ScrollProps } from 'components/HOCs/WithViewportMeta'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import getDefaultEventData from 'components/Trackers/defaults'
import { TestIDs } from 'test/utils/testIds'
import { style } from 'theme'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { withEnv } from 'utils/environment'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { isDefaultFlow } from 'utils/FlowProvider/utils'

import { REFERRAL_BANNER_HEIGHT } from '../../ReferralBanner'
import {
  AddedProps,
  EnhancedFullNavProps,
  FullNavProps,
  InnerProps,
  ScrollBehaviorProps,
} from './types'

export const borderStyle = (hasBorder: boolean) => `
  border-bottom: 1px solid rgba(25, 25, 25, ${hasBorder ? '0.04' : '0'});
  transition: border-bottom 250ms ease;
`

const Container = styled.div<Partial<EnhancedFullNavProps> & { hasBorder: boolean }>`
  align-items: center;
  background-color: ${style(theme => theme.colors.white)};
  ${props =>
    props.hideShadow &&
    'box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.13), 0 1px 0 0 rgba(0, 0, 0, 0.04);'}
  display: flex;
  height: 80px;
  justify-content: center;
  position: fixed;
  top: ${props => (props.hasBanner ? '44px' : '0')};
  left: 0;
  width: 100vw;
  min-width: 100%;
  z-index: 10;
  box-shadow: 0px 3px 15px 2px rgba(0, 0, 0, 0.02);
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    box-shadow: none;
    height: 66px;
    ${props => borderStyle(props.hasBorder)}
  }
`

const ContainerShim = styled.div<Partial<InnerProps & FullNavProps>>`
  background-color: transparent;
  height: ${props => {
    let shimHeight = 80
    shimHeight += props.hasBanner ? REFERRAL_BANNER_HEIGHT : 0
    shimHeight += props.noShim ? 0 : 30

    return shimHeight + 'px'
  }};
  @media (max-width: ${style(theme => theme.breakpoints.md)}px) {
    height: ${props => {
      let shimHeight = 80
      shimHeight += props.hasBanner ? REFERRAL_BANNER_HEIGHT : 0
      shimHeight += props.noShim ? 0 : 30

      return shimHeight + 'px'
    }};
  }

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    height: ${props => {
      let shimHeight = 50
      shimHeight += props.hasBanner ? REFERRAL_BANNER_HEIGHT : 0
      shimHeight += props.noShim ? 0 : 20

      return shimHeight + 'px'
    }};
  }
`

const StyledHeader = styled.div`
  font-size: 14px;
  line-height: 22px;
`

const LinkButton = styled.a`
  color: ${style(theme => theme.colors.slate)};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  text-decoration: none;
  outline: none;
  background: none;
  position: relative;
  cursor: pointer;
  border-radius: 100px;
  border: solid 1px ${style(theme => theme.colors.stoneLight)};
  background: none;
  padding: 7px 15px;
  user-select: none;

  &:hover {
    background: ${style(theme => theme.colors.ivory)};
  }

  @media (max-width: 550px) {
    border: none;
    font-size: 12px;
    color: ${style(theme => theme.colors.stone)};
    padding: 0;
    &:hover {
      background: none;
    }
  }
  @media (min-width: 551px) {
    width: 85px;
  }
`

const LinkButtonTextContainer = styled.div`
  @media (min-width: 551px) {
    text-align: center;
    margin: 4px;
  }
`

const ActionButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  left: 40px;
  position: absolute;
  @media (max-width: ${style(theme => theme.breakpoints.md)}px) {
    left: 30px;
  }
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    left: 20px;
  }
`

const StyledProgressBar = styled(DiscreteProgressBar)`
  @media (max-width: ${style(theme => theme.breakpoints.md)}px) {
    max-width: 250px;
  }
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    max-width: 200px;
  }
`

const BackImage = styled.img`
  width: 24px;
  height: 24px;
`

const AcornsHeaderLogo = styled.img`
  height: 26px;
`

const getSkipClickTracker = props => {
  const TrackedLinkButton = withClickTrack<any, any>(props.skipClickTracker)(LinkButton)

  return (
    <TrackedLinkButton
      target={props.linkTarget}
      href={props.linkRef}
      onClick={props.onLink}
      data-testid={TestIDs.NAV.LINK}
    >
      <Ink opacity={0.02} />
      <LinkButtonTextContainer>{props.linkText}</LinkButtonTextContainer>
    </TrackedLinkButton>
  )
}

export const enhance = compose<EnhancedFullNavProps, FullNavProps>(
  withEnv,
  withHeroText,
  withRouter,
  withFlowRouter,
  withHandlers({
    onBack: ({
      history,
      location,
      env,
      route,
      backClickOriginEventData,
      backClickTracker,
      backClickEventDataOverride,
    }) => () => {
      const fromRedirectLink = location?.state?.fromRedirectLink
      const currentRoute = location?.pathname

      if (backClickEventDataOverride) {
        trackEvent('Button Tapped', { eventData: backClickEventDataOverride })
      } else if (backClickTracker) {
        trackEvent('Button Tapped', {
          eventData: {
            ...getButtonTappedEvent(backClickTracker),
            ...getDefaultEventData('Button Tapped'),
            ...(backClickOriginEventData &&
              history.location.state?.referrer && { origin: history.location.state.referrer }),
          },
        })
      }

      if (fromRedirectLink && currentRoute?.includes('manual-funding-source')) {
        return route({ from: FlowRoute.ManualFundingSource, action: 'skip' })
      } else {
        const url = env.get('webAppUrl')
        return location?.search === '?flow=laterOnboarding'
          ? window.location.replace(url)
          : history.goBack()
      }
    },
  }),
  withProps<InnerProps, FullNavProps & AddedProps>(
    ({
      heroText,
      hideProgressBar,
      match,
      flowProgress,
      partnerLogo,
      referralAmount,
      showAcornsLogo,
      onBackOverride,
    }) => {
      const hasBanner = !!partnerLogo || !!referralAmount

      const { stepIndex: index, stepPartialProgress: percentage, steps } = flowProgress(match.path)

      if (!match.isExact) {
        return {
          hasBanner,
          index: 0,
          steps,
        }
      }

      return {
        hasBanner,
        heroText,
        index,
        percentage,
        showAcornsLogo: showAcornsLogo ?? false,
        complete: match.path === FlowRoute.Success,
        steps,
        hideProgressBar: hideProgressBar || isDefaultFlow(),
        onBackOverride,
      }
    },
  ),
  withViewportMeta,
  withProps<ScrollBehaviorProps, ScrollProps>(props => ({
    hasScrolled: props.scrollTop > 0,
  })),
)

export const FullNav: React.SFC<EnhancedFullNavProps> = props => (
  <>
    <Container hasBanner={props.hasBanner} hasBorder={!props.hideBorder && props.hasScrolled}>
      {props.showAcornsLogo && <AcornsHeaderLogo src={AcornsLogoGreenSVG} />}
      {!props.showAcornsLogo && !props.hideHeader && (props.headerText || props.heroText) && (
        <StyledHeader data-testid={TestIDs.NAV.HEADER}>
          <strong>{props.headerText || props.heroText.header}</strong>
        </StyledHeader>
      )}
      {!props.hideProgressBar && (
        <StyledProgressBar
          index={props.index}
          progress={props.percentage && props.percentage}
          steps={props.steps}
          responsiveSteps
          showAllLabels
          complete={props.complete}
        />
      )}
      {props.back && (
        <ActionButton
          onClick={props.onBackOverride ?? props.onBack}
          data-testid={TestIDs.NAV.BACK_BUTTON}
        >
          <BackImage src={BackArrowSVG} />
        </ActionButton>
      )}

      <Accessibility>
        {props.skipClickTracker ? (
          getSkipClickTracker(props)
        ) : props.linkText ? (
          <LinkButton
            target={props.linkTarget}
            href={props.linkRef}
            onClick={props.onLink}
            data-testid={TestIDs.NAV.LINK}
          >
            <Ink opacity={0.02} />
            <LinkButtonTextContainer>{props.linkText}</LinkButtonTextContainer>
          </LinkButton>
        ) : null}
      </Accessibility>
    </Container>
    {!props.hideShimContainer && (
      <ContainerShim hasBanner={props.hasBanner} noShim={props.noShim} />
    )}
  </>
)

export default enhance(FullNav)
